import type { StorefrontProduct, ProductCategory } from '@sifly/shared-types'

/**
 * Category tiles for the Catalog template (CatalogHome).
 *
 * The category-tile thumbnails use the first product in each category's
 * primary photo — zero config, looks good when products have curated
 * imagery. CATEGORY_TILE_IMAGES overrides that for categories whose first
 * product photo reads badly as a tile (e.g. a board shot that is really a
 * full-set photo, duplicating the eFoil Sets tile beside it).
 *
 * Some categories share one tile (CATEGORY_GROUPS): wing sets, hydrofoil wings
 * and stabilizers are one "Wings" tile, and accessories and parts are one
 * "Parts & Accessories" tile. Inside a group tile a type filter narrows to one
 * category, and the featured order follows the group's listed order.
 */

// Categories that share one tile, in the order they list inside it. Visitors
// don't see the inventory split: a wing set and a single wing are the same
// wings, and nobody shopping can tell a "part" from an "accessory".
export const CATEGORY_GROUPS = {
  wings: ['wing_set', 'wing', 'stabilizer'],
  accessories: ['accessory', 'parts'],
} as const satisfies Record<string, readonly ProductCategory[]>

export type GroupKey = keyof typeof CATEGORY_GROUPS
export type GroupedCategory = (typeof CATEGORY_GROUPS)[GroupKey][number]

// Labels for the filter inside a group tile.
export const SUBTYPE_LABELS: Record<GroupedCategory, string> = {
  wing_set: 'Wing sets',
  wing: 'Hydrofoil wings',
  stabilizer: 'Stabilizers',
  accessory: 'Accessories',
  parts: 'Parts',
}

export const GROUP_ALL_LABELS: Record<GroupKey, string> = {
  wings: 'All wings',
  accessories: 'All parts & accessories',
}

export function isGroupKey(k: string | null | undefined): k is GroupKey {
  return k != null && Object.hasOwn(CATEGORY_GROUPS, k)
}

function groupOf(c: string | null | undefined): GroupKey | null {
  for (const g of Object.keys(CATEGORY_GROUPS) as GroupKey[]) {
    if ((CATEGORY_GROUPS[g] as readonly string[]).includes(c ?? '')) return g
  }
  return null
}

// A tile is a lone category, a group, or everything.
export type TileKey = Exclude<ProductCategory, GroupedCategory> | GroupKey | 'all'

// Tile labels. Keep in sync with `ProductCategory` in
// shared-types/database.types.ts (TS enforces completeness).
export const TILE_LABELS: Record<Exclude<TileKey, 'all'>, string> = {
  efoil_set: 'eFoil Sets',
  board: 'Boards',
  hydrofoil_unit: 'Hydrofoil Units',
  wings: 'Wings',
  battery: 'Batteries',
  accessories: 'Parts & Accessories',
  foil_assist: 'Foil Assist',
}

function tileKeyFor(category: ProductCategory): Exclude<TileKey, 'all'> {
  return groupOf(category) ?? (category as Exclude<ProductCategory, GroupedCategory>)
}

// Display order when a tile has ≥1 product. eFoil Sets first (marquee), then
// complete-system parts (boards, foils, wings), then power (batteries, foil
// assist), then add-ons. Unknown categories fall to the end.
const TILE_ORDER: Exclude<TileKey, 'all'>[] = [
  'efoil_set',
  'board',
  'hydrofoil_unit',
  'wings',
  'battery',
  'foil_assist',
  'accessories',
]

export const DEFAULT_CATEGORY: TileKey = 'efoil_set'

// Curated tile images, shared by every tenant. Each points at an asset in the
// SiFly platform tenant's media library (the same owner as HERO_FALLBACK_IMAGE),
// as the w1920 derivative the image picker emits; the tile crops it to `sq`.
const CATEGORY_TILE_IMAGES: Partial<Record<TileKey, string>> = {
  // Complete Stellar S set (board, mast, foil) on white: leads with the core
  // product instead of whichever catalog item sorts first (media_items.id d180e198-…).
  all:
    'https://media.siflyfoils.com/00000000-0000-0000-0000-00000005f1ff/d180e198-0c0b-4cfa-af39-18c7e6cdc166/original.webp/w1920',
  // Red Stellar board, top-down on white. Red, not the blue board, because the
  // All Products tile beside it already shows the blue set (media_items.id 344a172e-…).
  board:
    'https://media.siflyfoils.com/00000000-0000-0000-0000-00000005f1ff/344a172e-8382-4b10-956f-93a6928c6e99/original.webp/w1920',
  // Red VORTEC propeller on white, so the merged tile reads as gear rather
  // than whichever part sorts first (media_items.id 3983f7c2-…).
  accessories:
    'https://media.siflyfoils.com/00000000-0000-0000-0000-00000005f1ff/3983f7c2-2b88-4ade-92da-70c5ab26f08f/original.webp/w1920',
}

type CategoryTile = {
  key: TileKey
  label: string
  photo: string | null
  count: number
}

// Featured order inside a group: by the group's listed order (wing sets, then
// single wings, then stabilizers), each keeping the catalog's own order
// (Array.sort is stable).
export function groupOrder(list: StorefrontProduct[], group: GroupKey): StorefrontProduct[] {
  const order = CATEGORY_GROUPS[group] as readonly string[]
  const rank = (p: StorefrontProduct) => order.indexOf(p.category ?? '')
  return [...list].sort((a, b) => rank(a) - rank(b))
}

// Featured order on All Products: by tile, in the row's own order (eFoil sets
// first, parts and accessories last), then by the group's listed order, each
// keeping the catalog's own order. Without it a part with a low sort_order
// led the page ahead of the eFoil sets.
export function allOrder(list: StorefrontProduct[]): StorefrontProduct[] {
  const rank = (p: StorefrontProduct) => {
    if (!p.category) return Number.MAX_SAFE_INTEGER
    const tile = TILE_ORDER.indexOf(tileKeyFor(p.category))
    const group = groupOf(p.category)
    const sub = group ? (CATEGORY_GROUPS[group] as readonly string[]).indexOf(p.category) : 0
    return (tile === -1 ? TILE_ORDER.length : tile) * 100 + sub
  }
  return [...list].sort((a, b) => rank(a) - rank(b))
}

// Whether a product belongs in a tile (or /products category option) and,
// inside a group, the narrowed type. Shared by both pages so a group key like
// 'wings' is never compared straight against a product's own category.
export function matchesSelection(
  p: StorefrontProduct,
  tile: TileKey,
  subtype: GroupedCategory | 'all',
): boolean {
  if (tile === 'all') return true
  if (!p.category || tileKeyFor(p.category) !== tile) return false
  return subtype === 'all' || p.category === subtype
}

export function buildCategoryTiles(products: StorefrontProduct[]): CategoryTile[] {
  const byTile = new Map<Exclude<TileKey, 'all'>, StorefrontProduct[]>()
  for (const p of products) {
    if (!p.category) continue
    const key = tileKeyFor(p.category)
    const list = byTile.get(key) ?? []
    list.push(p)
    byTile.set(key, list)
  }

  function tilePhoto(list: StorefrontProduct[]): string | null {
    return list.find((q) => q.photos[0])?.photos[0] ?? null
  }

  function tile(key: TileKey, list: StorefrontProduct[]): CategoryTile {
    const ordered = isGroupKey(key) ? groupOrder(list, key) : list
    return {
      key,
      label: key === 'all' ? 'All Products' : (TILE_LABELS[key] ?? key),
      photo: CATEGORY_TILE_IMAGES[key] ?? tilePhoto(ordered),
      count: list.length,
    }
  }

  const tiles: CategoryTile[] = [tile('all', products)]
  for (const key of TILE_ORDER) {
    const list = byTile.get(key)
    if (!list || list.length === 0) continue
    tiles.push(tile(key, list))
    byTile.delete(key)
  }
  // Any leftover categories (e.g. a new enum value not yet in TILE_ORDER)
  // append at the end so they don't silently disappear.
  for (const [key, list] of byTile) tiles.push(tile(key, list))
  return tiles
}

// `subtype` narrows a group tile to one of its categories.
export type Selection = { tile: TileKey; subtype: GroupedCategory | 'all' }

// `?category=wings&type=wing_set`. A bare grouped category (`?category=wing_set`,
// the shape before the merge and what dealer-authored category links still
// use) opens its group pre-filtered to that category.
export function parseSelection(category: string | null, type: string | null): Selection {
  const group = groupOf(category)
  if (group) return { tile: group, subtype: category as GroupedCategory }
  if (isGroupKey(category)) {
    return { tile: category, subtype: groupOf(type) === category ? (type as GroupedCategory) : 'all' }
  }
  if (category === 'all') return { tile: 'all', subtype: 'all' }
  if (category && Object.hasOwn(TILE_LABELS, category)) return { tile: category as TileKey, subtype: 'all' }
  return { tile: DEFAULT_CATEGORY, subtype: 'all' }
}
